import React, { Children } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, User, Building2, Compass } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
export function SummaryPage() {
  const navigate = useNavigate();
  const {
    profile,
    preferences,
    business,
    completeOnboarding
  } = useAuth();
  const handleComplete = () => {
    completeOnboarding();
    navigate('/app');
  };
  const getRecommendedPath = () => {
    if (preferences?.startWith === 'personal') return 'We recommend starting with your Personal Legal Health Check.';
    if (preferences?.startWith === 'business') return 'We recommend starting with your Business Legal Health Check.';
    if (preferences?.startWith === 'both') return 'We recommend starting with your Personal Legal Health Check, then moving to Business.';
    return 'We recommend starting with your Personal Legal Health Check.';
  };
  return <div className="min-h-screen bg-aliceBlue flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1390">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl" data-id="element-1391">
        <div className="text-center mb-8" data-id="element-1392">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4" data-id="element-1393">
            <CheckCircle className="h-8 w-8 text-green-600" data-id="element-1394" />
          </div>
          <h2 className="text-3xl font-bold text-primary" data-id="element-1395">Setup Complete</h2>
          <p className="mt-2 text-lg text-secondary" data-id="element-1396">
            Your profile is ready. Let's get started on your assessment.
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-1397">
          <div className="bg-white py-6 px-6 shadow-sm sm:rounded-xl border border-borderColor" data-id="element-1398">
            <div className="flex items-center gap-3 mb-4 border-b border-borderColor pb-4" data-id="element-1399">
              <Compass className="w-6 h-6 text-primary" data-id="element-1400" />
              <h3 className="text-xl font-bold text-primary" data-id="element-1401">
                Recommended Starting Path
              </h3>
            </div>
            <p className="text-primary font-medium mb-4" data-id="element-1402">
              {getRecommendedPath()}
            </p>

            {preferences?.motivations && preferences.motivations.length > 0 && <div data-id="element-1403">
                <p className="text-sm text-secondary mb-2" data-id="element-1404">
                  Based on your interests:
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-1405">
                  {preferences.motivations.map(m => <span key={m} className="px-3 py-1 bg-aliceBlue text-primary text-xs font-medium rounded-full border border-borderColor" data-id="element-1406">
                      {m}
                    </span>)}
                </div>
              </div>}
          </div>

          <div className="grid sm:grid-cols-2 gap-6" data-id="element-1407">
            <div className="bg-white py-6 px-6 shadow-sm sm:rounded-xl border border-borderColor h-full" data-id="element-1408">
              <div className="flex items-center gap-3 mb-4 border-b border-borderColor pb-4" data-id="element-1409">
                <User className="w-6 h-6 text-turquoise" data-id="element-1410" />
                <h3 className="text-xl font-bold text-primary" data-id="element-1411">
                  Personal Profile
                </h3>
              </div>
              <div className="space-y-3" data-id="element-1412">
                <div data-id="element-1413">
                  <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1414">
                    Province
                  </p>
                  <p className="font-medium text-primary" data-id="element-1415">
                    {profile?.province || '-'}
                  </p>
                </div>
                <div data-id="element-1416">
                  <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1417">
                    Completing For
                  </p>
                  <p className="font-medium text-primary capitalize" data-id="element-1418">
                    {profile?.completingFor || '-'}
                  </p>
                </div>
                <div data-id="element-1419">
                  <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1420">
                    Children/Dependants
                  </p>
                  <p className="font-medium text-primary" data-id="element-1421">
                    {profile?.children || '-'}
                  </p>
                </div>
                <div data-id="element-1422">
                  <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1423">
                    Property Owner
                  </p>
                  <p className="font-medium text-primary" data-id="element-1424">
                    {profile?.property || '-'}
                  </p>
                </div>
              </div>
            </div>

            {business?.hasBusiness && <div className="bg-white py-6 px-6 shadow-sm sm:rounded-xl border border-borderColor h-full" data-id="element-1425">
                <div className="flex items-center gap-3 mb-4 border-b border-borderColor pb-4" data-id="element-1426">
                  <Building2 className="w-6 h-6 text-violetBlue" data-id="element-1427" />
                  <h3 className="text-xl font-bold text-primary" data-id="element-1428">
                    Business Profile
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-1429">
                  <div data-id="element-1430">
                    <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1431">
                      Business Name
                    </p>
                    <p className="font-medium text-primary truncate" data-id="element-1432">
                      {business.name || '-'}
                    </p>
                  </div>
                  <div data-id="element-1433">
                    <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1434">
                      Type
                    </p>
                    <p className="font-medium text-primary" data-id="element-1435">
                      {business.type || '-'}
                    </p>
                  </div>
                  <div data-id="element-1436">
                    <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1437">
                      Province
                    </p>
                    <p className="font-medium text-primary" data-id="element-1438">
                      {business.province || '-'}
                    </p>
                  </div>
                  <div data-id="element-1439">
                    <p className="text-xs text-secondary uppercase tracking-wider" data-id="element-1440">
                      Employees
                    </p>
                    <p className="font-medium text-primary" data-id="element-1441">
                      {business.employees || '-'}
                    </p>
                  </div>
                </div>
              </div>}
          </div>

          <div className="pt-4" data-id="element-1442">
            <button onClick={handleComplete} className="w-full flex justify-center py-4 px-4 border border-transparent rounded-lg shadow-sm text-lg font-semibold text-white bg-violetBlue hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violetBlue transition-opacity" data-id="element-1443">
              Go to My Legal Health Check
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}